/**
 * Report builder - multi-step wizard
 */

import React, { useState } from 'react';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Select } from '../UI/Select';
import { FileText, Plus, Trash2, AlertCircle } from 'lucide-react';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useReportJobSubmit } from '../../hooks/useReportJob';
import { ReportQuestion } from '../../types/api';
import { CustomSectionsTab } from './CustomSectionsTab';

interface ReportBuilderProps {
  availableTypes?: Array<{ value: string; label: string }>;
  onComplete?: (reportId: string) => void;
}

export function ReportBuilder({ availableTypes, onComplete }: ReportBuilderProps = {}) {
  const { projectId } = useWorkspace();
  const [step, setStep] = useState(1);
  const [reportType, setReportType] = useState('');
  const [clientName, setClientName] = useState('');
  const [questions, setQuestions] = useState<ReportQuestion[]>([]);
  
  const submitMutation = useReportJobSubmit();
  
  // NO DEFAULTS - MUST come from policy pack via availableTypes prop
  // If no types provided, show error instead of defaults
  const typeOptions = availableTypes || [];
  
  // Debug logging
  React.useEffect(() => {
    console.log('[ReportBuilder] Received availableTypes prop:', availableTypes);
    console.log('[ReportBuilder] Report types loaded:', typeOptions.length);
    console.log('[ReportBuilder] Type options:', typeOptions);
    if (typeOptions.length === 0) {
      console.error('[ReportBuilder] ERROR: No report types provided! Must pass availableTypes from policy pack.');
    }
  }, [availableTypes, typeOptions]);

  const handleAddQuestion = () => {
    setQuestions([...questions, { section: '', question: '' }]);
  };

  const handleRemoveQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleQuestionChange = (index: number, field: 'section' | 'question', value: string) => {
    const newQuestions = [...questions];
    newQuestions[index][field] = value;
    setQuestions(newQuestions);
  };

  const handleSubmit = async () => {
    if (!projectId) return;

    try {
      const result = await submitMutation.mutateAsync({
        project_id: projectId,
        report_type: reportType as any,
        client_name: clientName,
        questions,
        enable_cache: true,
      });
      
      // Call onComplete if provided
      if (onComplete && result?.output_data?.report_id) {
        onComplete(result.output_data.report_id);
      }
    } catch (error) {
      console.error('Report generation failed:', error);
    }
  };

  if (!projectId) {
    return (
      <Card>
        <p className="text-center py-12 text-gray-500">Select a project to create reports</p>
      </Card>
    );
  }

  if (!typeOptions || typeOptions.length === 0) {
    return (
      <Card>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
          <p className="text-gray-900 dark:text-gray-100 font-semibold mb-2">No Report Types Available</p>
          <p className="text-gray-600 text-sm">
            Report types must be provided from the project's policy pack.
            <br />
            Navigate to the project-specific reports page or configure the policy pack.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card title="Create Report" subtitle={`Step ${step} of 4`}>
        {step === 1 && (
          <div className="space-y-4">
            <Select
              label="Report Type"
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              options={typeOptions}
            />
            {typeOptions.length > 1 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2 text-sm text-blue-900">
                <AlertCircle className="h-4 w-4" />
                <span>
                  Showing {typeOptions.length - 1} report types from policy pack
                </span>
              </div>
            )}
            <Input
              label="Client Name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Enter client name"
            />
            <Button onClick={() => setStep(2)} disabled={!reportType || !clientName}>
              Next
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Questions</h3>
              <Button size="sm" onClick={handleAddQuestion}>
                <Plus className="h-4 w-4" />
                Add Question
              </Button>
            </div>

            {questions.map((q, idx) => (
              <div key={idx} className="flex gap-2">
                <Input
                  placeholder="Section"
                  value={q.section}
                  onChange={(e) => handleQuestionChange(idx, 'section', e.target.value)}
                  className="w-48"
                />
                <Input
                  placeholder="Question"
                  value={q.question}
                  onChange={(e) => handleQuestionChange(idx, 'question', e.target.value)}
                  className="flex-1"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRemoveQuestion(idx)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button onClick={() => setStep(3)} disabled={questions.length === 0}>
                Next
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-900 dark:text-blue-200">
                <strong>Note:</strong> Custom sections can be added after report generation. 
                You can add them in the Report Viewer after the report is created.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button onClick={() => setStep(4)}>
                Next: Review
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <h4 className="font-medium mb-2">Report Summary</h4>
              <p><strong>Type:</strong> {reportType}</p>
              <p><strong>Client:</strong> {clientName}</p>
              <p><strong>Questions:</strong> {questions.length}</p>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(3)}>
                Back
              </Button>
              <Button onClick={handleSubmit} isLoading={submitMutation.isPending}>
                <FileText className="h-4 w-4" />
                Generate Report
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

